import { Box, Button, Modal, Skeleton, Tooltip } from '@sendible/design-system/src';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { useGetMessage, useDeleteMessage } from '@sendible/frontendv2/src/models/calendar';
import { ActionGroup, DeleteModalActions, Header, HeaderContent } from '../../index.styles';
import { useCalendarContext, useCalendarSidebarContext } from '../../../../context';
import { editMessage, fullDateToMonthString } from '../../../../utils';

export const MessagesSidebarHeader = () => {
  const { changeSidebarMode, closeSidebar } = useCalendarContext();
  const { lastSidebarMode, sidebarMessageId, sidebarDay } = useCalendarSidebarContext();
  const { data: message, isLoading } = useGetMessage(sidebarMessageId);
  const { t } = useTranslation('calendar');
  const user = useBridgeUser();
  const deleteMessage = useDeleteMessage();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const triggerEditMessage = (statusOverride?: CalendarStatuses) => {
    if (!message) return;

    const date = fullDateToMonthString(message.sendDateLocalized);
    const offset = user.timezone;

    editMessage(statusOverride ?? message.status, message.id, date, offset);
    closeSidebar();
  };

  const handleEdit = () => triggerEditMessage();

  const handleRepost = () => {
    const repostStatus = message?.status === 'Failed' || message?.status === 'Bounced' ? 'Sent' : message?.status;

    triggerEditMessage(repostStatus);
  };

  const handleDeleteRequest = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!message) return;

    deleteMessage.mutate(message.id);
    setIsDeleteConfirmOpen(false);
  };

  const renderActions = () => {
    if (!message) return null;

    const { status } = message;
    const isSentOrFailed = status === 'Sent' || status === 'Failed' || status === 'Bounced';
    const isDraftOrScheduled = status === 'Draft' || status === 'Pending' || status === 'Queued' || status === 'Approved';
    const isRejected = status === 'Rejected';
    const isForApproval = status === 'ForApproval';

    return (
      <>
        {isForApproval && (
          <Tooltip content={t('review')}>
            <Button
              onClick={handleEdit}
              icon="edit_alt"
              size={14}
            />
          </Tooltip>
        )}
        {isDraftOrScheduled && (
          <Tooltip content={t('edit')}>
            <Button
              onClick={handleEdit}
              icon="edit"
              size={14}
            />
          </Tooltip>
        )}
        {isRejected && (
          <Tooltip content={t('resubmit')}>
            <Button
              onClick={handleEdit}
              icon="corner_down_left"
              size={14}
            />
          </Tooltip>
        )}
        {isSentOrFailed && (
          <Tooltip content={t('repost')}>
            <Button
              onClick={handleRepost}
              icon="send"
              size={14}
            />
          </Tooltip>
        )}
        <Tooltip content={t('delete')}>
          <Button
            onClick={handleDeleteRequest}
            icon="trash"
            size={14}
          />
        </Tooltip>
      </>
    );
  };

  return (
    <Header>
      {isDeleteConfirmOpen && (
        <Modal
          close={() => setIsDeleteConfirmOpen(false)}
          customClass="global-modal"
          headingTitle={t('delete_confirm_title') as string}
          testId="delete-post-modal"
          variation="small"
          ariaCloseLabel={t('close') as string}
        >
          <Box
            orientation="column"
            flex={1}
          >
            <p>{t('delete_confirm_text')}</p>
            <DeleteModalActions contentJustify="flex-end">
              <Button
                onClick={() => setIsDeleteConfirmOpen(false)}
                label={t('delete_confirm_cancel') as string}
                size={16}
                variation="outline"
                autoFocus
              />
              <Button
                appearance="primary"
                onClick={handleDeleteConfirm}
                label={t('delete_confirm_yes') as string}
                isLoading={deleteMessage.isPending}
                size={16}
                variation="fill"
              />
            </DeleteModalActions>
          </Box>
        </Modal>
      )}
      <HeaderContent data-testid="calendar-sidebar-header-content">
        <ActionGroup>
          <Tooltip content={t('close')}>
            <Button
              onClick={closeSidebar}
              icon="close"
              size={14}
            />
          </Tooltip>
          {lastSidebarMode === 'day' && (
            <Tooltip content={t('back')}>
              <Button
                onClick={() => changeSidebarMode('day', sidebarDay)}
                icon="chevron_left"
                size={14}
              />
            </Tooltip>
          )}
        </ActionGroup>
        <ActionGroup>
          {isLoading || !message ? (
            <Skeleton
              height="2.5rem"
              width="5rem"
            />
          ) : (
            renderActions()
          )}
        </ActionGroup>
      </HeaderContent>
    </Header>
  );
};
